<template>
  <div class="visual-editor">
    <bubble-menu
      v-if="editor"
      :editor="editor"
      :tippy-options="{ duration: 150, theme: 'visual-editor' }"
    >
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            small
            icon
            :class="{ 'primary--text': editor.isActive('bold') }"
            @click="editor.chain().focus().toggleBold().run()"
          >
            <v-icon small>format_bold</v-icon>
          </v-btn>
        </template>
        <span>ตัวหนา (Bold)</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            small
            icon
            :class="{ 'primary--text': editor.isActive('italic') }"
            @click="editor.chain().focus().toggleItalic().run()"
          >
            <v-icon small>format_italic</v-icon>
          </v-btn>
        </template>
        <span>ตัวเอียง (Italic)</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            small
            icon
            :class="{ 'primary--text': editor.isActive({ textAlign: 'left' }) }"
            @click="editor.chain().focus().setTextAlign('left').run()"
          >
            <v-icon small>format_align_left</v-icon>
          </v-btn>
        </template>
        <span>ชิดซ้าย (Align left)</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            small
            icon
            :class="{ 'primary--text': editor.isActive({ textAlign: 'right' }) }"
            @click="editor.chain().focus().setTextAlign('right').run()"
          >
            <v-icon small>format_align_right</v-icon>
          </v-btn>
        </template>
        <span>ชิดขวา (Align right)</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            small
            icon
            :class="{ 'primary--text': editor.isActive({ textAlign: 'justify' }) }"
            @click="editor.chain().focus().setTextAlign('justify').run()"
          >
            <v-icon small>format_align_justify</v-icon>
          </v-btn>
        </template>
        <span>เต็มบรรทัด (Justify)</span>
      </v-tooltip>
    </bubble-menu>

    <div
      v-if="slashMenuOpen && filteredSlashItems.length"
      class="slash-menu elevation-6"
    >
      <v-list dense>
        <v-list-tile
          v-for="item in filteredSlashItems"
          :key="item.label"
          @click="applySlashCommand(item)"
        >
          <v-list-tile-content>
            <v-list-tile-title>{{ item.label }}</v-list-tile-title>
            <v-list-tile-sub-title v-if="item.description">
              {{ item.description }}
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list>
    </div>

    <editor-content
      v-if="editor"
      :editor="editor"
    ></editor-content>
  </div>
</template>

<script>
import { Editor, EditorContent, BubbleMenu } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';

export default {
  name: 'VisualEditor',
  components: {
    EditorContent,
    BubbleMenu,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      editor: null,
      slashMenuOpen: false,
      slashMenuQuery: '',
      slashMenuItems: [
        {
          label: 'Paragraph',
          description: 'ข้อความปกติ',
          command: (chain) => chain.setParagraph(),
        },
        {
          label: 'Heading 1',
          description: 'หัวข้อใหญ่',
          command: (chain) => chain.toggleHeading({ level: 1 }),
        },
        {
          label: 'Heading 2',
          description: 'หัวข้อรอง',
          command: (chain) => chain.toggleHeading({ level: 2 }),
        },
        {
          label: 'Bullet list',
          description: 'รายการแบบจุด',
          command: (chain) => chain.toggleBulletList(),
        },
        {
          label: 'Numbered list',
          description: 'รายการแบบลำดับเลข',
          command: (chain) => chain.toggleOrderedList(),
        },
        {
          label: 'Quote',
          description: 'ข้อความอ้างอิง',
          command: (chain) => chain.toggleBlockquote(),
        },
      ],
    };
  },
  computed: {
    filteredSlashItems() {
      const query = (this.slashMenuQuery || '').toLowerCase();
      if (!query) {
        return this.slashMenuItems;
      }
      return this.slashMenuItems.filter((item) =>
        item.label.toLowerCase().includes(query)
      );
    },
  },
  watch: {
    value(newValue) {
      if (!this.editor) {
        return;
      }
      const current = this.editor.getHTML();
      if ((newValue || '') !== current) {
        this.editor.commands.setContent(newValue || '', false);
      }
    },
  },
  mounted() {
    this.editor = new Editor({
      extensions: [
        StarterKit,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
      ],
      content: this.value || '',
      onUpdate: ({ editor }) => {
        this.handleSlashMenuUpdate(editor);
        const html = editor.getHTML();
        this.$emit('input', html);
        this.$emit('change', html);
      },
    });
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  },
  methods: {
    handleSlashMenuUpdate(editor) {
      const state = editor.state;
      const { from } = state.selection;
      const textBefore = state.doc.textBetween(
        Math.max(0, from - 50),
        from,
        '\n',
        '\0'
      );
      const match = textBefore.match(/\/([^\s/]*)$/);

      if (match) {
        this.slashMenuOpen = true;
        this.slashMenuQuery = match[1] || '';
      } else {
        this.slashMenuOpen = false;
        this.slashMenuQuery = '';
      }
    },
    applySlashCommand(item) {
      if (!this.editor) {
        return;
      }

      const editor = this.editor;
      const { state } = editor;
      const { from } = state.selection;
      const textBefore = state.doc.textBetween(
        Math.max(0, from - 50),
        from,
        '\n',
        '\0'
      );
      const match = textBefore.match(/\/([^\s/]*)$/);

      if (!match) {
        this.slashMenuOpen = false;
        this.slashMenuQuery = '';
        return;
      }

      const deleteFrom = from - match[0].length;

      const chain = editor
        .chain()
        .focus()
        .deleteRange({ from: deleteFrom, to: from });

      item.command(chain).run();

      this.slashMenuOpen = false;
      this.slashMenuQuery = '';
    },
  },
};
</script>

<style scoped>
.visual-editor {
  border: 1px solid #ccc;
  position: relative;
  border-radius: 4px;
  min-height: 200px;
  padding: 8px;
  cursor: text;
}

::v-deep .visual-editor .ProseMirror {
  min-height: 200px;
}

.slash-menu {
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: #ffffff;
  border-radius: 4px;
  z-index: 10;
}

/* Bubble menu appearance (Tippy theme) */
::v-deep .tippy-box[data-theme~='visual-editor'] {
  background-color: #ffffff;
  border-radius: 4px;
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.25);
}
</style>
